/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import can from 'can';
import $ from 'jquery';
import Construct from 'can/construct/construct';
import device from 'scripts/utils/device';

export default Construct.extend({
    scrollTop(topPosition) {
        can.$('html,body').animate({scrollTop: topPosition}, '500');
    },
    getViewportSize: function () {
        return {
            width: $(window).width(),
            height: $(window).height()
        };
    },

    isOnScreen: function (element, respectTopBorder) {
        element = element[0] || element;
        if (! (element && element instanceof Element)) {
            return false;
        }
        var parent = element;
        while (parent) {
            if (window.getComputedStyle(parent).display === 'none') {
                return false;
            }
            parent = parent.parentElement;
        }

        var rect = element.getBoundingClientRect();
        if (respectTopBorder) {
            if (rect.bottom < 0) {
                return false;
            }
        }
        return rect.top < window.innerHeight;
    },

    pushState: function (url) {
        window.history.pushState(null, '', url);
    },

    getElementCssProperty: function ($element, name, units) {
        units = units || 'px';
        var value = $element.css(name);
        var regexp = new RegExp('^(\\d+(.\\d+)?)' + units + '$', 'i');
        var matches = regexp.exec(value);
        if (matches) {
            return parseInt(matches[1], 10);
        }
        return null;
    },

    getMeta: function () {
        var raw = {};
        raw.title = can.$('title').html();
        can.$('raw').each(function (i, element) {
            var $tag = can.$(element);
            var name = $tag.attr('name') || $tag.attr('itemprop') || $tag.attr('property');
            if (name) {
                raw[name] = $tag.attr('content');
            }
        });
        return raw;
    },

    parseSocialMeta: function () {
        var raw = this.getMeta();

        var params = {
            title: '',
            description: '',
            url: '',
            network: {
                google: {},
                twitter: {}
            }
        };

        params.title = raw.title;
        params.description = raw.description || '';
        params.url = location.origin + location.pathname;

        if (raw['og:title']) {
            params.title = raw['og:title'];
        }
        if (raw['og:description']) {
            params.description = raw['og:description'];
        }
        if (raw['og:url']) {
            params.url = raw['og:url'];
        }

        // Reserved for future use.
        if (raw.name) {
            params.network.google.title = raw.name;
        }

        if (raw['twitter:title']) {
            params.network.twitter.title = raw['twitter:title'];
        }
        if (raw['twitter:description']) {
            params.network.twitter.description = raw['twitter:description'];
        }

        return params;
    },

    reverseChildItems: function (container) {
        var parent = container.parentNode;
        parent.removeChild(container);

        var childNodes = container.childNodes;
        var fragment = document.createDocumentFragment();
        for (var i = childNodes.length - 1; i >= 0; i--) {
            fragment.appendChild(childNodes[i]);
        }
        while (container.firstChild) {
            container.removeChild(container.firstChild);
        }
        container.appendChild(fragment);
        parent.appendChild(container);
    },

    watchElementSize: function ($element, callback, interval) {
        var width = -1;
        var height = -1;
        var intervalId = setInterval(function () {
            var elementWidth = $element.width();
            var elementHeight = $element.height();
            if (width !== elementWidth || height !== elementHeight) {
                width = elementWidth;
                height = elementHeight;
                callback(width, height);
            }
        }, interval);

        return function () {
            clearInterval(intervalId);
        };
    },

    addScript: function(src, callback) {
        let script = document.createElement('script'),
            loaded;

        script.setAttribute('src', src);
        if (callback) {
            script.onreadystatechange = script.onload = function() {
                if (!loaded) {
                    callback();
                }
                loaded = true;
            };
        }
        document.getElementsByTagName('head')[0].appendChild(script);
    },

    isBrowserInFullscreenMode: function() {
        if (document.fullScreen || document.mozFullScreen || document.webkitIsFullScreen ||
            document.msFullscreenElement) {
            return true;
        }

        const ieVersion = device.isIE();

        if (ieVersion && ieVersion <= 10) {
            return 1 >= window.outerHeight - window.innerHeight;
        }

        return false;
    },

    trapFocusByWrapper: function($wrapEl) {
        const KEY_CODE_TAB = 9;
        const focusableEls = $wrapEl.find(
            'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])',
        );
        const firstFocusableEl = focusableEls[0];
        const lastFocusableEl = focusableEls[focusableEls.length - 1];

        $wrapEl.on('keydown', function(e) {
            const isTabPressed = e.key === 'Tab' || e.keyCode === KEY_CODE_TAB;
            if (!isTabPressed) {
                return;
            }

            if (e.shiftKey) {
                if (document.activeElement === firstFocusableEl) {
                    lastFocusableEl.focus();
                    e.preventDefault();
                }
            } else {
                if (document.activeElement === lastFocusableEl) {
                    firstFocusableEl.focus();
                    e.preventDefault();
                }
            }
        });
    },

    trapFocusMultiplatform: function($wrapEl) {
        const focusableEls = $wrapEl.find(
            'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])',
        );
        const firstFocusableEl = focusableEls[0];
        const lastFocusableEl = focusableEls[focusableEls.length - 1];
        this.setFocusEvents(firstFocusableEl, lastFocusableEl, focusableEls);
        this.setFocusEvents(lastFocusableEl, firstFocusableEl, focusableEls);
    },

    setFocusEvents: function (el, anotherSideEl, listAvailableEls) {
        let toggleEl = null;
        $(el).off('focus');
        $(el).on( 'focus', function(e) {
            if (document.activeElement === anotherSideEl) {
                toggleEl = el;
                e.preventDefault();
            }
            if (document.activeElement === el) {
                toggleEl = anotherSideEl;
                e.preventDefault();
            }
        });
        $(el).off('blur');
        $(el).on('blur', function(e) {
            setTimeout(function () {
                for(var i=0; i < listAvailableEls.length; i++ ) {
                    if (listAvailableEls[i] === document.activeElement) {
                        toggleEl = null;
                    }
                }
                if (toggleEl) {
                    toggleEl.focus();
                    toggleEl = null;
                    e.preventDefault();
                }
            }, 500);
        });
    }
}, {});
